import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Bell, Moon, Sun, Shield, LogOut, LogIn, Home, Compass, BookOpen, Heart, User, Download, Trophy, MessageCircle, Gift, X, Hourglass, WifiOff } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose,
} from "@/components/ui/sheet";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { avatarUrl, api } from "@/lib/appresse";
import { useChallenge, AMBASSADOR_VISIBLE, fmtCompact } from "@/lib/challenge";
import { Wordmark } from "@/components/Wordmark";

const LinkRow = ({ to, icon: Icon, label, testid }) => (
  <SheetClose asChild>
    <Link to={to} data-testid={testid}
      className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-secondary transition-colors">
      <Icon className="w-5 h-5 text-primary" /> <span className="font-medium">{label}</span>
    </Link>
  </SheetClose>
);

const avatarInitials = (u) => {
  const f = (u.first_name || "").trim();
  const l = (u.last_name || "").trim();
  if (f || l) return ((f[0] || "") + (l[0] || "")).toUpperCase();
  const parts = (u.name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (u.email || "?")[0].toUpperCase();
};

export const TopBar = ({ hideMenu = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const ch = useChallenge();
  const chActive = !ch.loading && ch.active;
  const [dark, setDark] = useState(() => (localStorage.getItem("appresse_theme") || "light") === "dark");
  const [notifOpen, setNotifOpen] = useState(false);
  const [amb, setAmb] = useState(null);
  const [nudge, setNudge] = useState(false);
  const [nudgeOptOut, setNudgeOptOut] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);

  // L'espace ambassadeur est réservé aux ambassadeurs désignés + admins/super
  // admins — jamais visible pour un client normal (payant ou non), quel que soit
  // l'état de la monétisation. Les clients disposent des Points Capital à la place.
  const showAmbassador = AMBASSADOR_VISIBLE && !!user && (!!user.ambassador || user.role === "admin");

  useEffect(() => {
    if (!user) { setNotifs([]); setUnread(0); return; }
    const load = () => api.myNotifications().then((d) => { setNotifs(d.notifications || []); setUnread(d.unread || 0); }).catch(() => {});
    load();
    const i = setInterval(load, 20000);
    return () => clearInterval(i);
  }, [user]);

  const openNotifs = () => {
    setNotifOpen(true);
    if (unread > 0) api.myNotificationsRead().then(() => setUnread(0)).catch(() => {});
  };
  const clickNotif = (n) => {
    setNotifOpen(false);
    if (n.url) navigate(n.url.replace(/^https?:\/\/[^/]+/, ""));
  };

  useEffect(() => {
    if (!user) { setAmb(null); return; }
    const load = () => api.myAmbassador().then((d) => setAmb(d.totals)).catch(() => {});
    load();
    window.addEventListener("appresse:ambassador-refresh", load);
    return () => window.removeEventListener("appresse:ambassador-refresh", load);
  }, [user]);

  // Humorous share nudge: shown while the ambassador has never shared and hasn't
  // opened their share center. Imposed twice; from the 3rd time a discreet
  // "Ça ne m'intéresse pas" opt-out appears and stops it for good.
  useEffect(() => {
    if (!showAmbassador || !user || amb == null) { setNudge(false); return; }
    const totalShares = (amb.shares || 0) + (amb.site_shares || 0);
    let off = false, count = 0;
    try {
      off = localStorage.getItem(`appresse_amb_nudge_off_${user.id}`) === "1";
      count = parseInt(localStorage.getItem(`appresse_amb_nudge_count_${user.id}`) || "0", 10) || 0;
    } catch { /* ignore */ }
    if (totalShares > 0 || off) { setNudge(false); return; }
    const t = setTimeout(() => {
      setNudgeOptOut(count >= 2); // already imposed twice → allow permanent opt-out
      setNudge(true);
      try { localStorage.setItem(`appresse_amb_nudge_count_${user.id}`, String(count + 1)); } catch { /* ignore */ }
    }, 1500);
    return () => clearTimeout(t);
  }, [user, amb, showAmbassador]);

  const nudgeClose = useCallback(() => setNudge(false), []);
  const nudgeGoCenter = useCallback(() => {
    setNudge(false);
    if (user) { try { localStorage.setItem(`appresse_amb_nudge_off_${user.id}`, "1"); } catch { /* ignore */ } }
    api.ambassadorNudge("click").catch(() => {});
    navigate("/ambassadeur");
  }, [user, navigate]);
  const nudgeOptOutNow = useCallback(() => {
    setNudge(false);
    if (user) { try { localStorage.setItem(`appresse_amb_nudge_off_${user.id}`, "1"); } catch { /* ignore */ } }
    api.ambassadorNudge("optout").catch(() => {});
  }, [user]);

  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("appresse_theme", next ? "dark" : "light");
  };

  return (
    <header data-testid="topbar" className="shrink-0 z-40 backdrop-blur-md bg-background/95 border-b border-border" style={{ paddingTop: "max(env(safe-area-inset-top), 0.75rem)" }}>
      <div className="max-w-6xl mx-auto px-3 sm:px-6 h-16 flex items-center gap-2">
        {hideMenu ? (
          <div className="w-10 shrink-0" aria-hidden />
        ) : (
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="menu-button" aria-label="Menu">
              <Menu className="w-6 h-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-80" style={{ paddingTop: "calc(env(safe-area-inset-top) + 1.5rem)" }}>
            <SheetHeader>
              <SheetTitle className="text-left">
                <Wordmark imgClass="h-7 w-auto" taglineClass="text-[10px]" />
              </SheetTitle>
            </SheetHeader>
            <nav className="mt-4 space-y-1">
              <LinkRow to="/" icon={Home} label="Accueil" testid="menu-accueil" />
              {user && <LinkRow to="/explorer" icon={Compass} label="Explorer" testid="menu-explorer" />}
              {user && <LinkRow to="/mes-revues" icon={BookOpen} label="Mes revues" testid="menu-revues" />}
              {user && <LinkRow to="/favoris" icon={Heart} label="Mes favoris" testid="menu-favoris" />}
              {user && <LinkRow to="/hors-ligne" icon={WifiOff} label="Mes revues hors-ligne" testid="menu-hors-ligne" />}
              {showAmbassador && <LinkRow to="/ambassadeur" icon={Trophy} label="Espace ambassadeur" testid="menu-ambassadeur" />}
              {showAmbassador && <LinkRow to="/ambassadeur?chat=1" icon={MessageCircle} label="Chat dev dispo" testid="menu-chat-dev" />}
              <LinkRow to="/compte" icon={User} label={user ? "Mon compte" : "Mon espace"} testid="menu-compte" />
              {user?.role === "admin" && <LinkRow to="/admin" icon={Shield} label="Administration" testid="menu-admin" />}
            </nav>
            <div className="mt-4 pt-4 border-t border-border space-y-1">
              <button onClick={() => window.dispatchEvent(new Event("appresse:open-install"))} data-testid="menu-download-app"
                className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-secondary text-primary font-semibold">
                <Download className="w-5 h-5" /> <span>Télécharger l'application</span>
              </button>
              <button onClick={toggleTheme} data-testid="menu-theme" className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-secondary">
                {dark ? <Sun className="w-5 h-5 text-primary" /> : <Moon className="w-5 h-5 text-primary" />}
                <span className="font-medium">{dark ? "Thème clair" : "Thème sombre"}</span>
              </button>
              {user && (
                <button onClick={() => { logout(); navigate("/"); }} data-testid="menu-logout" className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-secondary text-destructive">
                  <LogOut className="w-5 h-5" /> <span className="font-medium">Déconnexion</span>
                </button>
              )}
            </div>
          </SheetContent>
        </Sheet>
        )}

        <Link to="/" className="flex-1 flex flex-col items-center leading-none" data-testid="logo-home-link">
          <img src="/logo.png" alt="APP PRESSE" className="h-6 sm:h-7 w-auto dark:hidden" />
          <span className="hidden dark:inline font-serif text-xl tracking-tight">
            <span className="font-black text-white">APP</span>{" "}
            <span className="font-semibold text-primary">PRESSE</span>
          </span>
          <span className="text-[11px] sm:text-xs font-serif font-medium text-primary/85 mt-0.5">Le monde s'ouvre à vous</span>
        </Link>

        <Button variant="ghost" size="icon" data-testid="bell-button" aria-label="Notifications"
          onClick={openNotifs} className="relative">
          <Bell className="w-6 h-6" />
          {unread > 0 && (
            <span className="absolute top-0 right-0 min-w-4 h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="bell-badge">{unread}</span>
          )}
        </Button>

        <Dialog open={notifOpen} onOpenChange={setNotifOpen}>
          <DialogContent className="w-[calc(100vw-1.5rem)] max-w-sm rounded-2xl p-4 sm:p-5 overflow-hidden" data-testid="notif-dialog">
            <div className="flex flex-col gap-2 pt-1 min-w-0">
              <h3 className="font-serif font-black text-lg text-center mb-1">Notifications</h3>
              {notifs.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">Aucune notification pour le moment.<br />Vous êtes à jour&nbsp;!</p>
              ) : (
                <div className="max-h-[70vh] sm:max-h-80 overflow-y-auto space-y-1.5 -mx-1 px-1">
                  {notifs.map((n) => (
                    <button key={n.id} onClick={() => clickNotif(n)} data-testid={`notif-item-${n.id}`}
                      className={`w-full text-left rounded-xl px-3 py-2.5 transition-colors hover:bg-secondary ${n.read ? "bg-transparent" : "bg-primary/5"}`}>
                      <div className="flex items-start gap-1.5 min-w-0">
                        {!n.read && <span className="w-2 h-2 mt-1 rounded-full bg-primary shrink-0" />}
                        <span className="text-sm font-semibold leading-snug break-words line-clamp-2 min-w-0 flex-1">{n.title}</span>
                      </div>
                      <div className="text-xs text-muted-foreground leading-snug break-words line-clamp-2 mt-0.5">{n.body}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {showAmbassador && (
          <div className="relative shrink-0 flex items-center gap-1.5">
            {chActive && (
              <Link to="/ambassadeur" data-testid="challenge-countdown-badge" aria-label="Fin du challenge ambassadeur"
                className="flex items-center gap-1 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 px-2 h-9 tabular-nums font-bold text-xs animate-pulse">
                <Hourglass className="w-3.5 h-3.5" />
                {fmtCompact(ch.parts)}
              </Link>
            )}
            <Link to="/ambassadeur" data-testid="ambassador-badge" aria-label="Espace ambassadeur"
              onClick={nudgeGoCenter}
              className="flex items-center gap-1 rounded-full bg-primary/10 hover:bg-primary/20 text-primary px-2.5 h-9 transition-colors">
              <Trophy className={`w-4 h-4 ${chActive ? "animate-pulse text-amber-500" : ""}`} />
              <span className="text-xs font-bold tabular-nums">{((amb?.shares || 0) + (amb?.site_shares || 0))}</span>
              <span className="text-[10px] opacity-70">🔗</span>
              <span className="text-xs font-bold tabular-nums">{amb?.referrals || 0}</span>
              <span className="text-[10px] opacity-70">👥</span>
            </Link>
            {nudge && (
              <div data-testid="ambassador-nudge"
                className="absolute right-0 top-11 z-50 w-64 animate-in fade-in slide-in-from-top-2 duration-300">
                <div className="absolute -top-1.5 right-8 w-3 h-3 rotate-45 bg-primary" />
                <div className="relative rounded-2xl bg-primary text-primary-foreground shadow-xl p-3.5">
                  <button onClick={nudgeClose} data-testid="ambassador-nudge-close"
                    aria-label="Fermer"
                    className="absolute top-2 right-2 text-primary-foreground/70 hover:text-primary-foreground">
                    <X className="w-4 h-4" />
                  </button>
                  <p className="text-sm font-bold flex items-center gap-1.5 pr-5">
                    <Gift className="w-4 h-4 shrink-0" /> Psst… ambassadeur !
                  </p>
                  <p className="text-xs mt-1 leading-snug opacity-95">
                    On vous voit toujours pas dans le tableau 👀 Filez à votre centre de partage et gagnez des cadeaux !
                  </p>
                  <button onClick={nudgeGoCenter} data-testid="ambassador-nudge-cta"
                    className="mt-2.5 w-full rounded-full bg-white text-primary font-semibold text-sm py-1.5 hover:bg-white/90 transition-colors">
                    Partager maintenant
                  </button>
                  {nudgeOptOut && (
                    <button onClick={nudgeOptOutNow} data-testid="ambassador-nudge-optout"
                      className="mt-2 w-full text-center text-[11px] text-primary-foreground/70 hover:text-primary-foreground underline">
                      Ça ne m'intéresse pas
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <Link to="/compte" data-testid="topbar-avatar-link" aria-label="Mon compte" className="shrink-0">
          {user ? (
            user.avatar_url ? (
              <img src={avatarUrl(user.id, user.last_activity_at || "")} alt={user.name}
                className="w-9 h-9 rounded-full object-cover border-2 border-primary/30 hover:border-primary transition-colors"
                data-testid="topbar-avatar-img" />
            ) : (
              <span className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-serif font-black text-sm border-2 border-primary/20 hover:border-primary transition-colors"
                data-testid="topbar-avatar-initial">
                {avatarInitials(user)}
              </span>
            )
          ) : (
            <span className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-muted-foreground hover:text-primary transition-colors" data-testid="topbar-avatar-guest">
              <User className="w-5 h-5" />
            </span>
          )}
        </Link>
      </div>
    </header>
  );
};
